import { format } from "date-fns";
import { useMemo, useState } from "react";
import { Link } from "react-router";
import MainLayout from "@/components/layouts";
import SimpleTable, { ColumnDef } from "@/components/shared/simple-table";
import { FilterItem } from "@/interfaces";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useSubs } from "./subs.hooks";
import { SubsWithJoin } from "./subs.entity";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar.tsx";
import { getInitials } from "@/lib/utils.ts";
import { MoreHorizontal } from "lucide-react";
import {
  DropdownMenuContent,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { InviteSubsModal } from "./components/invite-subs-modal";
import type { SubcontractSortKey, SubcontractStatus } from "@/repositories/subs.repository";

const sortKeyOf = (key: string): SubcontractSortKey => (key === "email" ? "email" : "name");

const statusOf = (value: string): SubcontractStatus => (value === "ended" ? "ended" : "live");

export default function SubsPage() {
  const {
    subs,
    total,
    page,
    pageSize,
    name,
    service,
    setPage,
    setPageSize,
    setName,
    setService,
    sortKey,
    sortOrder,
    setSortKey,
    status,
    setStatus,
    removeSubs,
    openInviteModal,
    inviteOpen,
    inviteSaving,
    inviteError,
    sendInvite,
    handleSubsSubmit,
  } = useSubs();

  const [removeId, setRemoveId] = useState<string | null>(null);

  const serviceOptions = useMemo(() => {
    const names = Array.from(new Set(subs.flatMap((s) => s.services.map((svc) => svc.name))));
    return names.map((n) => ({ label: n, value: n }));
  }, [subs]);

  const columns: ColumnDef<SubsWithJoin>[] = [
    {
      header: "Organization",
      accessor: (row) => (
        <Link to={`/subs/${row.id}`} className="hover:underline flex items-center">
          <Avatar className="w-8 h-8 mr-4">
            <AvatarImage src={row.subcontractor.logo ?? undefined} />
            <AvatarFallback>{getInitials(row.subcontractor.name)}</AvatarFallback>
          </Avatar>
          <span className="flex flex-col">
            {row.subcontractor.name}
            {row.endedAt && <span className="text-xs text-muted-foreground">Ended {format(new Date(row.endedAt), "MMM d, yyyy")}</span>}
          </span>
        </Link>
      ),
      sortable: true,
      sortKey: "name",
    },
    {
      header: "Email",
      accessor: (row) => row.subcontractor.email,
      sortable: true,
      sortKey: "email",
    },
    {
      header: "Services",
      accessor: (row) => {
        const services = row.services.map((s) => s.name);
        if (services.length === 0) return "—";
        if (services.length <= 2) return services.join(", ");
        return `${services.slice(0, 2).join(", ")} +${services.length - 2} more`;
      },
    },
    {
      header: "Primary Contact",
      accessor: (row) => {
        const { contact } = row;
        return contact ? (
          <div className="flex flex-col">
            <span className="font-medium">{contact.name}</span>
            <span className="text-xs text-muted-foreground">{contact.email}</span>
            <span className="text-xs text-muted-foreground">{contact.phone}</span>
          </div>
        ) : (
          "—"
        );
      },
    },
    {
      header: "Actions",
      accessor: (row) => (
        <div className="flex gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="icon" variant="ghost">
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <Link to={`/subs/${row.id}`}>
                <DropdownMenuItem>View Details</DropdownMenuItem>
              </Link>
              {!row.endedAt && (
                <DropdownMenuItem
                  onSelect={() => {
                    setTimeout(() => setRemoveId(row.id), 0);
                  }}
                  className="text-destructive"
                >
                  Remove Subs
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <Dialog open={removeId === row.id} onOpenChange={(open) => setRemoveId(open ? row.id : null)}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Remove Subs?</DialogTitle>
              </DialogHeader>
              <p className="pt-2 text-sm text-muted-foreground">
                Are you sure you want to remove <strong>{row.subcontractor.name}</strong>? You can
                re-invite them later if needed.
              </p>
              <DialogFooter className="pt-4">
                <Button variant="outline" onClick={() => setRemoveId(null)}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={async () => {
                    setRemoveId(null);
                    await removeSubs(row.id);
                    toast.success("Subs removed successfully.");
                  }}
                >
                  Remove
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      ),
      className: "whitespace-nowrap",
    },
  ];

  const filterItems: FilterItem[] = [
    {
      type: "search",
      title: "Organization Name",
      value: name,
      onValueChange: setName,
    },
    {
      type: "select",
      title: "Service",
      value: service,
      onValueChange: setService,
      options: serviceOptions,
    },
    {
      type: "select",
      title: "Status",
      value: status,
      onValueChange: (value: string) => setStatus(statusOf(value)),
      options: [
        { label: "Current", value: "live" },
        { label: "Ended", value: "ended" },
      ],
    },
  ];

  return (
    <MainLayout
      breadcrumb={[{ name: "Subs", href: "#" }]}
      pageHeader={{
        title: "Companies",
        subtitle: "Manage your sub contractor companies and their services",
        buttonText: "+ Invite New Company",
        onButtonClick: () => openInviteModal(true),
      }}
    >
      <SimpleTable<SubsWithJoin>
        columns={columns}
        data={subs}
        totalCount={total}
        page={page}
        pageSize={pageSize}
        setPage={setPage}
        setPageSize={setPageSize}
        sortKey={sortKey}
        sortOrder={sortOrder}
        onSortChange={(key) => setSortKey(sortKeyOf(key))}
        filters={filterItems}
        onReset={() => {
          setName("");
          setService("");
          setStatus("live");
        }}
      />

      <InviteSubsModal
        open={inviteOpen}
        saving={inviteSaving}
        error={inviteError}
        onOpenChange={openInviteModal}
        onSubmit={handleSubsSubmit}
      />
    </MainLayout>
  );
}
